// DN10 — línea "Pendiente de validar" compartida por las pantallas de dinero
// del directivo (Dashboard, Líneas, D3 mensual, D4 cliente).
//
// La UNIDAD de validación es el BLOQUE, nunca el paquete. Un bloque sin validar
// puede tener 0 paquetes y aun así costar mano de obra (por ejemplo 0 paq,
// $3.000 de MO, −$3.000 de utilidad): decidir por paquetes ocultaría ese
// importe y afirmaría "todo validado" cuando no lo está. El API lo entrega en
// `pendiente_validacion*.bloques` (corridas en tiendas).

import { miles } from './formato';

/** Lo capturado sin validar que el API declara junto a cada bloque de dinero. */
export interface PendienteDeclarado {
  bloques: number;
  paquetes: number;
  valor_producido: number;
  costo_mano_obra: number;
  utilidad: number;
}

const CERO: PendienteDeclarado = {
  bloques: 0,
  paquetes: 0,
  valor_producido: 0,
  costo_mano_obra: 0,
  utilidad: 0,
};

/** ¿Queda algo sin validar? Se decide por bloques (ver cabecera). */
export function hayPendiente(...partes: Array<{ bloques: number } | null | undefined>): boolean {
  return partes.some((p) => (p?.bloques ?? 0) > 0);
}

/** Consolida varias partes (líneas + tiendas) en un solo pendiente declarado. */
export function sumarPendientes(
  ...partes: Array<PendienteDeclarado | null | undefined>
): PendienteDeclarado {
  return partes.reduce<PendienteDeclarado>(
    (a, p) => ({
      bloques: a.bloques + (p?.bloques ?? 0),
      paquetes: a.paquetes + (p?.paquetes ?? 0),
      valor_producido: a.valor_producido + (p?.valor_producido ?? 0),
      costo_mano_obra: a.costo_mano_obra + (p?.costo_mano_obra ?? 0),
      utilidad: a.utilidad + (p?.utilidad ?? 0),
    }),
    { ...CERO },
  );
}

/**
 * Texto de la línea. `monto` llega YA formateado con el formateador de pesos de
 * la pantalla, y se muestra SIEMPRE que haya bloques pendientes, aunque los
 * paquetes sean cero. `etiqueta` aclara qué es el monto ("de utilidad").
 */
export function textoPendiente(
  p: { bloques: number; paquetes: number } | null | undefined,
  monto: string,
  etiqueta?: string,
): string {
  const bloques = p?.bloques ?? 0;
  if (bloques <= 0) return 'Pendiente de validar: ninguno — todo validado';
  const paquetes = p?.paquetes ?? 0;
  const conteo = `${miles(bloques)} ${bloques === 1 ? 'bloque' : 'bloques'}`;
  const paq = paquetes > 0 ? ` · ${miles(paquetes)} paq` : '';
  return `Pendiente de validar: ${conteo}${paq} · ${monto}${etiqueta ? ` ${etiqueta}` : ''} — no suma`;
}
